// In-overlay map browser: lists locally available maps and, when the server
// has import enabled, browses a GitHub repository and imports maps on demand.
// Selecting or importing a map navigates to ?map=<name>, which re-boots the
// game on that map through the existing boot path in main.ts.

import {
  listMaps,
  browseRemote,
  importMap,
  RemoteMapError,
  MapServerUnreachableError,
  type RemoteMap,
} from './mapclient'

const STOP_EVENTS = ['click', 'mousedown', 'mouseup', 'pointerdown', 'pointerup'] as const

export function mountMapBrowser(panel: HTMLElement, currentMap: string): void {
  panel.replaceChildren()
  panel.classList.add('maps-panel')
  // Keep interactions inside the panel from reaching the overlay, whose click
  // handler grabs pointer lock and starts the game.
  for (const ev of STOP_EVENTS) {
    panel.addEventListener(ev, (e) => e.stopPropagation())
  }

  panel.append(buildLocalSection(currentMap), buildImportSection())
}

function playMap(name: string): void {
  const url = new URL(window.location.href)
  url.searchParams.set('map', name)
  window.location.href = url.toString()
}

// ─── Local maps ──────────────────────────────────────────────────────────────

function buildLocalSection(currentMap: string): HTMLElement {
  const section = sectionEl('Local maps')
  const list = div('map-chips')
  const status = div('maps-status')
  status.textContent = 'Loading…'
  section.append(list, status)

  void listMaps()
    .then((names) => {
      status.remove()
      if (names.length === 0) {
        status.textContent = 'No maps on the server.'
        section.append(status)
        return
      }
      for (const name of names.sort()) {
        const chip = document.createElement('button')
        chip.type = 'button'
        chip.className = 'map-chip'
        chip.textContent = name
        if (name === currentMap) {
          chip.classList.add('current')
          chip.title = 'Currently loaded'
        }
        chip.addEventListener('click', () => playMap(name))
        list.append(chip)
      }
    })
    .catch((err) => {
      status.textContent = describeError(err)
    })

  return section
}

// ─── Import from GitHub ──────────────────────────────────────────────────────

function buildImportSection(): HTMLElement {
  const section = sectionEl('Import from GitHub')

  const form = div('maps-import-form')
  const input = document.createElement('input')
  input.type = 'text'
  input.className = 'maps-input'
  input.placeholder = 'owner/repo  ·  owner/repo/subdir  ·  github.com URL'
  input.autocapitalize = 'off'
  input.autocomplete = 'off'
  input.spellcheck = false

  const browseBtn = document.createElement('button')
  browseBtn.type = 'button'
  browseBtn.className = 'maps-btn'
  browseBtn.textContent = 'Browse'

  form.append(input, browseBtn)

  const status = div('maps-status')
  const results = div('map-results')
  section.append(form, status, results)

  const runBrowse = (repo: string): void => {
    results.replaceChildren()
    status.textContent = repo ? `Browsing ${repo}…` : 'Loading default repository…'
    void browseRemote(repo, '')
      .then((listing) => {
        status.textContent =
          listing.maps.length === 0
            ? `No maps found in ${listing.repo}@${listing.ref}.`
            : `${listing.maps.length} map(s) in ${listing.repo}@${listing.ref}` +
              (listing.truncated ? ' (tree truncated — some maps may be hidden)' : '')
        for (const m of listing.maps) {
          results.append(buildResultRow(m, status))
        }
      })
      .catch((err) => {
        if (err instanceof RemoteMapError && err.disabled) {
          disableSection(section, form, status)
          return
        }
        // A missing default repo (400) on the initial load is not an error —
        // just prompt for input.
        if (!repo && err instanceof RemoteMapError && err.status === 400) {
          status.textContent = 'Enter a GitHub repository above to browse its maps.'
          return
        }
        status.textContent = describeError(err)
      })
  }

  browseBtn.addEventListener('click', () => runBrowse(input.value.trim()))
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') runBrowse(input.value.trim())
  })

  // Try the configured default repository on open.
  runBrowse('')

  return section
}

function buildResultRow(map: RemoteMap, status: HTMLElement): HTMLElement {
  const row = div('map-result')

  const meta = div('map-result-meta')
  const name = document.createElement('span')
  name.className = 'map-result-name'
  name.textContent = map.name
  const badge = document.createElement('span')
  badge.className = 'map-badge'
  badge.textContent = map.kind
  meta.append(name, badge)
  if (typeof map.size === 'number' && map.size > 0) {
    const size = document.createElement('span')
    size.className = 'map-result-size'
    size.textContent = formatBytes(map.size)
    meta.append(size)
  }

  const action = document.createElement('button')
  action.type = 'button'
  action.className = 'maps-btn'

  if (map.imported) {
    action.textContent = 'Play'
    action.addEventListener('click', () => playMap(map.name))
  } else {
    action.textContent = 'Import'
    action.addEventListener('click', () => {
      action.disabled = true
      action.textContent = 'Importing…'
      status.textContent = `Importing ${map.name}…`
      void importMap({ source: map.source })
        .then((res) => {
          status.textContent = `Imported ${res.name}. Loading…`
          playMap(res.name)
        })
        .catch((err) => {
          action.disabled = false
          action.textContent = 'Import'
          status.textContent = describeError(err)
        })
    })
  }

  row.append(meta, action)
  return row
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function disableSection(section: HTMLElement, form: HTMLElement, status: HTMLElement): void {
  form.querySelectorAll('input, button').forEach((el) => {
    ;(el as HTMLInputElement | HTMLButtonElement).disabled = true
  })
  status.textContent = 'Remote map import is disabled on this server.'
  section.classList.add('disabled')
}

function sectionEl(title: string): HTMLElement {
  const section = div('maps-section')
  const h = document.createElement('h2')
  h.className = 'maps-section-title'
  h.textContent = title
  section.append(h)
  return section
}

function div(className: string): HTMLDivElement {
  const el = document.createElement('div')
  el.className = className
  return el
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function describeError(err: unknown): string {
  if (err instanceof RemoteMapError) return err.message
  if (err instanceof MapServerUnreachableError) return 'Map server unreachable.'
  return err instanceof Error ? err.message : 'Unknown error.'
}
